import {memo, useEffect, useState} from 'react';

import ApartmentIcon from '@mui/icons-material/Apartment';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Home from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {LogoImageContainer, LogoImageSmallContainer} from './SideNav.style';
import LearnerPortalLogoDark from '../../../../assets/images/optix-academy-black.png';
import LearnerPortalLogoLight from '../../../../assets/images/optix-academy-white.png';
import Optix from '../../../../assets/images/rop.png';
import {OptixDialog} from '../../../../ui-lib/components/dialog/OptixDialog';
import {type NavigationLink} from '../../constants/navigation';
import {useSideNavigation} from '../../hooks/useSideNavigation';

interface SideNavigationProps {
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileToggle: () => void;
}

function SideNavigationComponent({
  isMobile,
  mobileOpen,
  onMobileToggle,
}: SideNavigationProps): JSX.Element {
  const {
    computedWidth,
    handleLogout,
    iconSx,
    logoutDialogOpen,
    navigate,
    onCloseLogoutDialog,
    onOpenLogoutDialog,
    toggleSidePanelExpanded,
    themeMode,
    validLinks,
    sidePanelExpanded,
    theme,
    organisationName,
    organisationGroupName,
    locationPathname,
  } = useSideNavigation({isMobile, mobileOpen, onMobileToggle});

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    validLinks.forEach((link) => {
      if (link.children?.some((child) => locationPathname === child.path)) {
        initial.add(link.path);
      }
    });
    return initial;
  });

  useEffect(() => {
    validLinks.forEach((link) => {
      if (link.children?.some((child) => locationPathname === child.path)) {
        setOpenGroups((prev) => new Set([...prev, link.path]));
      }
    });
  }, [locationPathname, validLinks]);

  const toggleGroup = (path: string): void => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const isGroupActive = (link: NavigationLink): boolean =>
    link.children?.some((child) => locationPathname === child.path) ?? false;

  const renderNavItem = (item: NavigationLink): JSX.Element => {
    const isActive = locationPathname === item.path;
    const button = (
      <ListItemButton
        selected={isActive}
        onClick={() => {
          navigate(item.path);
          if (isMobile) onMobileToggle();
        }}
        sx={iconSx}
        disabled={item.disabled ?? false}
      >
        <ListItemIcon
          sx={(t) => ({
            minWidth: sidePanelExpanded ? 40 : 32,
            color: isActive ? t.palette.primary.main : 'unset',
          })}
        >
          {item.icon}
        </ListItemIcon>
        {(sidePanelExpanded || isMobile) && (
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{fontWeight: isActive ? 600 : 400}}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.path} disablePadding sx={{display: 'block'}}>
        {sidePanelExpanded || isMobile ? (
          button
        ) : (
          <Tooltip title={item.name} placement="right">
            {button}
          </Tooltip>
        )}
      </ListItem>
    );
  };

  const renderGroupItem = (item: NavigationLink): JSX.Element => {
    const isOpen = openGroups.has(item.path);
    const groupActive = isGroupActive(item);

    if (!sidePanelExpanded && !isMobile) {
      const button = (
        <ListItemButton
          selected={groupActive}
          onClick={() => {
            toggleSidePanelExpanded();
            toggleGroup(item.path);
            navigate(item.path);
          }}
          sx={iconSx}
          disabled={item.disabled ?? false}
        >
          <ListItemIcon
            sx={(t) => ({
              minWidth: 32,
              color: groupActive ? t.palette.primary.main : 'unset',
            })}
          >
            {item.icon}
          </ListItemIcon>
        </ListItemButton>
      );
      return (
        <ListItem key={item.path} disablePadding sx={{display: 'block'}}>
          <Tooltip title={item.name} placement="right">
            {button}
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <ListItem key={item.path} disablePadding sx={{display: 'block'}}>
        <ListItemButton
          selected={groupActive && !isOpen}
          onClick={() => {
            toggleGroup(item.path);
          }}
          sx={iconSx}
          disabled={item.disabled ?? false}
        >
          <ListItemIcon
            sx={(t) => ({
              minWidth: 40,
              color: groupActive ? t.palette.primary.main : 'unset',
            })}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{fontWeight: groupActive ? 600 : 400}}
          />
          {isOpen ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {(item.children ?? []).map((child) => {
              const isActive = locationPathname === child.path;
              return (
                <ListItem
                  key={child.path}
                  disablePadding
                  sx={{display: 'block'}}
                >
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      navigate(child.path);
                      if (isMobile) onMobileToggle();
                    }}
                    sx={{
                      ...iconSx,
                      pl: 4,
                    }}
                  >
                    <ListItemIcon
                      sx={(t) => ({
                        minWidth: 32,
                        color: isActive ? t.palette.primary.main : 'unset',
                      })}
                    >
                      {child.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={child.name}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '0.875rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </ListItem>
    );
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: sidePanelExpanded ? 2 : 1,
            py: 1,
          }}
        >
          {sidePanelExpanded ? (
            <LogoImageContainer
              onClick={() => {
                navigate('/');
              }}
            >
              <img
                src={
                  themeMode === 'light'
                    ? LearnerPortalLogoDark
                    : LearnerPortalLogoLight
                }
                alt="Learner Portal"
              />
            </LogoImageContainer>
          ) : (
            <LogoImageSmallContainer
              onClick={() => {
                navigate('/');
              }}
            >
              <img src={Optix} alt="Learner Portal" />
            </LogoImageSmallContainer>
          )}
          <IconButton
            onClick={toggleSidePanelExpanded}
            size="small"
            sx={{ml: 'auto'}}
          >
            {sidePanelExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      )}

      {isMobile && <Box sx={{height: 64}} />}

      <List sx={{flex: 1, pt: 1, overflowY: 'auto', justifyContent: 'left'}}>
        {isMobile && location.pathname !== '/' && (
          <ListItemButton
            selected={false}
            onClick={() => {
              navigate('/');
              if (isMobile) onMobileToggle();
            }}
            sx={iconSx}
          >
            <ListItemIcon
              sx={(t) => ({
                minWidth: sidePanelExpanded ? 40 : 32,
                color: t.palette.primary.main,
              })}
            >
              <Home />
            </ListItemIcon>
            {(sidePanelExpanded || isMobile) && (
              <ListItemText
                primary={'Home'}
                primaryTypographyProps={{fontWeight: 400}}
              />
            )}
          </ListItemButton>
        )}
        {validLinks.map((item) =>
          item.children && item.children.length > 0
            ? renderGroupItem(item)
            : renderNavItem(item),
        )}
      </List>

      <Divider />
      <Tooltip
        title={
          sidePanelExpanded
            ? ''
            : organisationGroupName != null
              ? `${organisationName} · ${organisationGroupName}`
              : organisationName
        }
        placement="right"
      >
        <Box
          sx={{
            px: sidePanelExpanded ? 2 : 1,
            py: isMobile ? 0.5 : 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          <ApartmentIcon
            fontSize={isMobile ? 'medium' : 'large'}
            sx={{
              color: theme.palette.text.secondary,
              flexShrink: 0,
            }}
          />
          {(sidePanelExpanded || isMobile) && (
            <Box sx={{minWidth: 0}}>
              <Typography
                noWrap
                sx={{
                  display: 'block',
                  fontWeight: 600,
                  fontSize: isMobile ? '0.85rem' : '1.1rem',
                  color: theme.palette.text.primary,
                  lineHeight: 1.3,
                }}
              >
                {organisationName}
              </Typography>
              {organisationGroupName != null && (
                <Typography
                  noWrap
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.85rem' : '1.1rem',
                    color: theme.palette.text.secondary,
                    lineHeight: 1.3,
                  }}
                >
                  {organisationGroupName}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Tooltip>

      {isMobile && (
        <Box sx={{px: 1, pt: 1, pb: 2}}>
          <ListItemButton
            dense
            onClick={onOpenLogoutDialog}
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              py: 0.5,
            }}
          >
            <ListItemIcon
              sx={{color: theme.palette.primary.main, minWidth: 36}}
            >
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
          <OptixDialog
            open={logoutDialogOpen}
            onClose={onCloseLogoutDialog}
            title="Confirm Logout"
            message="Are you sure you want to log out?"
            enableAccept
            acceptText="Logout"
            acceptProps={{color: 'error'}}
            onAccept={handleLogout}
            enableDeny
            denyText="Cancel"
            maxWidth="sm"
            fullWidth
          />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{flexShrink: 0, width: isMobile ? 0 : computedWidth}}
      >
        <Drawer
          variant="temporary"
          open={isMobile && mobileOpen}
          onClose={onMobileToggle}
          ModalProps={{keepMounted: true}}
          sx={{
            display: isMobile ? 'block' : 'none',
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          open={!isMobile}
          sx={{
            display: !isMobile ? 'block' : 'none',
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: computedWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
}

export const SideNavigation = memo(SideNavigationComponent);
